// client/src/components/secure/training/AssignmentPlayer.js — take one assigned course:
// read each lesson (text, video, PDF), pass the quiz, sign the attestation.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Btn, Field, Markdown, Modal, Notice, Pill, fetchBlob, fmtDate, inputCls, openBlob } from './shared';
import SlidePlayer from './SlidePlayer';
import FlashcardDeck from './FlashcardDeck';

// Lessons that record "viewed" only when finished (last slide / every card got), not on opening.
export const INTERACTIVE = ['slides', 'flashcards'];

export const Lesson = ({ lesson, onOpenPdf, onComplete, completed }) => {
    if (lesson.lesson_type === 'slides') return <SlidePlayer key={lesson.lesson_id} content={lesson.content} notesMd={lesson.body_md} onComplete={onComplete} completed={completed} />;
    if (lesson.lesson_type === 'flashcards') return <FlashcardDeck key={lesson.lesson_id} content={lesson.content} onComplete={onComplete} completed={completed} />;
    if (lesson.lesson_type === 'video' && lesson.video) {
        const v = lesson.video;
        return (
            <div>
                {v.kind === 'iframe' && (
                    <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                        <iframe title={lesson.title} src={v.src} className="absolute inset-0 w-full h-full rounded" allow="fullscreen; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation" />
                    </div>
                )}
                {v.kind === 'video' && <video src={v.src} poster={v.poster || undefined} controls preload="metadata" className="w-full rounded" />}
                {v.kind === 'link' && <p className="text-sm">Watch: <a href={v.src} target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline">{v.src}</a></p>}
                {lesson.body_md && <div className="mt-3"><Markdown text={lesson.body_md} /></div>}
            </div>
        );
    }
    return (
        <div>
            {lesson.body_md && <Markdown text={lesson.body_md} />}
            {lesson.has_pdf && <Btn kind="ghost" className="mt-3" onClick={onOpenPdf}>Open the PDF: {lesson.pdf_name || 'document'}</Btn>}
        </div>
    );
};

const AssignmentPlayer = ({ assignmentId, onClose, onChanged }) => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [sign, setSign] = useState({ signature_name: '', programme: '', agree: false });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState('');

    const load = useCallback(async () => {
        try { setD(await api.get(`/api/training/assignments/${assignmentId}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, assignmentId]);
    useEffect(() => { load(); }, [load]);

    const lessons = useMemo(() => (d ? d.lessons : []), [d]);
    const questions = useMemo(() => (d ? d.questions : []), [d]);
    const mine = d && d.is_mine;
    const closed = d && ['completed', 'waived'].includes(d.assignment.status);
    const quizStep = lessons.length;
    const signStep = lessons.length + (questions.length ? 1 : 0);

    const markViewed = useCallback(async (lesson) => {
        if (!mine || closed || !lesson) return;
        try {
            await api.post(`/api/training/assignments/${assignmentId}/lessons/${lesson.lesson_id}/viewed`, {});
            setD((x) => (x && !x.viewed.includes(lesson.lesson_id) ? { ...x, viewed: [...x.viewed, lesson.lesson_id] } : x));
        } catch { /* shown on attest */ }
    }, [api, assignmentId, mine, closed]);
    useEffect(() => {
        if (d && step < lessons.length && !INTERACTIVE.includes(lessons[step].lesson_type)) markViewed(lessons[step]);
    }, [d, step, lessons, markViewed]);
    const finishLesson = useCallback(() => { if (step < lessons.length) markViewed(lessons[step]); }, [step, lessons, markViewed]);

    const openPdf = async (lesson) => {
        try { openBlob(await fetchBlob(`/api/training/lessons/${lesson.lesson_id}/pdf`), lesson.pdf_name || 'lesson.pdf'); } catch (e) { setError(e.message); }
    };
    const submitQuiz = async () => {
        setBusy(true); setError('');
        try {
            const r = await api.post(`/api/training/assignments/${assignmentId}/quiz`, { answers });
            setResult(r);
            if (r.completed) { setDone('Passed — training complete.'); onChanged && onChanged(); }
        } catch (e) { setError(e.message); }
        setBusy(false);
    };
    const attest = async () => {
        setBusy(true); setError('');
        try {
            const r = await api.post(`/api/training/assignments/${assignmentId}/attest`, sign);
            setDone(r.message || 'Recorded.');
            onChanged && onChanged();
            load();
        } catch (e) { setError(e.message); }
        setBusy(false);
    };

    if (!d) return <Modal title="Training" onClose={onClose}>{error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>}</Modal>;
    const c = d.course;
    const isHarassment = /harassment|bystander/i.test(c.course_key || '');
    return (
        <Modal title={c.title} onClose={onClose} wide>
            <div className="space-y-3">
                <div className="flex flex-wrap gap-2 items-center text-sm text-slate-600">
                    <Pill s={d.assignment.status} />
                    {c.status === 'draft' && <Pill s="draft">DRAFT — not approved</Pill>}
                    {d.assignment.due_date && <span>Due {fmtDate(d.assignment.due_date)}</span>}
                    <span>About {c.estimated_minutes} min</span>
                    {d.person && !mine && <span>— {d.person.name} (view only)</span>}
                </div>
                <Notice>{d.disclaimer}</Notice>
                {error && <Notice tone="bad">{error}</Notice>}
                {done && <Notice tone="ok">{done}</Notice>}
                {closed && d.assignment.status === 'completed' && (
                    <Notice tone="ok">Completed {fmtDate(d.assignment.completed_at)}{d.assignment.quiz_score != null ? ` · quiz ${d.assignment.quiz_score}%` : ''}{d.assignment.attestation_name ? ` · signed: ${d.assignment.attestation_name}` : ''}</Notice>
                )}

                <div className="flex flex-wrap gap-1 border-b pb-2">
                    {lessons.map((l, i) => (
                        <button key={l.lesson_id} type="button" onClick={() => setStep(i)}
                            className={`px-2 py-1 text-xs rounded ${step === i ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'}`}>
                            {i + 1}. {l.title}{d.viewed.includes(l.lesson_id) ? ' ✓' : ''}
                        </button>
                    ))}
                    {questions.length > 0 && <button type="button" onClick={() => setStep(quizStep)} className={`px-2 py-1 text-xs rounded ${step === quizStep ? 'bg-slate-800 text-white' : 'bg-slate-100'}`}>Quiz</button>}
                    {Number(c.requires_attestation) === 1 && <button type="button" onClick={() => setStep(signStep)} className={`px-2 py-1 text-xs rounded ${step === signStep ? 'bg-slate-800 text-white' : 'bg-slate-100'}`}>Sign</button>}
                </div>

                {step < lessons.length && (
                    <div>
                        <h3 className="font-semibold text-slate-800 mb-2">{lessons[step].title}</h3>
                        {INTERACTIVE.includes(lessons[step].lesson_type) && mine && !closed && !d.viewed.includes(lessons[step].lesson_id) && (
                            <p className="text-xs text-slate-500 mb-2">{lessons[step].lesson_type === 'slides' ? 'This lesson counts as done when you reach the last slide.' : 'This lesson counts as done when every card is marked "Got it".'}</p>
                        )}
                        <Lesson lesson={lessons[step]} onOpenPdf={() => openPdf(lessons[step])} onComplete={finishLesson} completed={d.viewed.includes(lessons[step].lesson_id)} />
                        <div className="flex justify-between mt-4">
                            <Btn kind="ghost" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Btn>
                            <Btn onClick={() => setStep(step + 1)}>{step + 1 < lessons.length ? 'Next lesson' : questions.length ? 'Go to the quiz' : 'Go to sign'}</Btn>
                        </div>
                    </div>
                )}

                {step === quizStep && questions.length > 0 && (
                    <div>
                        <p className="text-sm text-slate-600 mb-3">Pass mark {c.pass_mark}%. You may retake it.</p>
                        {questions.map((q, i) => {
                            const r = result && result.results.find((x) => x.question_id === q.question_id);
                            return (
                                <fieldset key={q.question_id} className="mb-4">
                                    <legend className="font-medium text-slate-800 mb-1">{i + 1}. {q.prompt}</legend>
                                    {q.choices.map((ch, j) => (
                                        <label key={j} className="flex gap-2 items-start text-sm py-0.5">
                                            <input type="radio" name={`q${q.question_id}`} checked={Number(answers[q.question_id]) === j && answers[q.question_id] !== undefined}
                                                disabled={!mine || closed} onChange={() => setAnswers({ ...answers, [q.question_id]: j })} />
                                            <span className={r && r.correct_index === j ? 'font-semibold text-emerald-700' : ''}>{ch}</span>
                                        </label>
                                    ))}
                                    {r && <p className={`text-xs mt-1 ${r.correct ? 'text-emerald-700' : 'text-red-700'}`}>{r.correct ? 'Correct.' : 'Not correct.'} {r.explanation || ''}</p>}
                                </fieldset>
                            );
                        })}
                        {result && <Notice tone={result.passed ? 'ok' : 'warn'}>Score {result.score}% ({result.correct} of {result.total}). {result.passed ? 'Passed.' : 'Not yet — review the lessons and try again.'}</Notice>}
                        {mine && !closed && (
                            <div className="flex justify-end gap-2 mt-3">
                                <Btn onClick={submitQuiz} disabled={busy || Object.keys(answers).length < questions.length}>Submit answers</Btn>
                                {result && result.passed && Number(c.requires_attestation) === 1 && <Btn kind="ok" onClick={() => setStep(signStep)}>Continue to sign</Btn>}
                            </div>
                        )}
                    </div>
                )}

                {step === signStep && Number(c.requires_attestation) === 1 && (
                    <div>
                        <Notice>{c.attestation_text || 'I have read and understand this training.'}</Notice>
                        {c.policy_doc_type && <p className="text-xs text-slate-600 mt-2">Signing also records your acknowledgement of the current {c.policy_doc_type.replace(/_/g, ' ')} in the Policy Center.</p>}
                        {mine && !closed ? (
                            <div className="mt-3">
                                {isHarassment && (
                                    <Field label="Programme used" hint="e.g. IDHR model training 2026, or the approved CE course name and provider.">
                                        <input className={inputCls} value={sign.programme} onChange={(e) => setSign({ ...sign, programme: e.target.value })} />
                                    </Field>
                                )}
                                <label className="flex gap-2 items-start text-sm mb-3">
                                    <input type="checkbox" checked={sign.agree} onChange={(e) => setSign({ ...sign, agree: e.target.checked })} />
                                    <span>I have read and understand this training.</span>
                                </label>
                                <Field label="Type your full name as your signature">
                                    <input className={inputCls} value={sign.signature_name} onChange={(e) => setSign({ ...sign, signature_name: e.target.value })} />
                                </Field>
                                <div className="flex justify-end"><Btn kind="ok" onClick={attest} disabled={busy || !sign.agree || sign.signature_name.trim().length < 3}>Sign and complete</Btn></div>
                            </div>
                        ) : !closed && <p className="text-sm text-slate-600 mt-3">Only {d.person ? d.person.name : 'the person assigned'} can sign this.</p>}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AssignmentPlayer;
